import { useSyncExternalStore } from 'react';
import { zh, type ZhKey } from './zh';
import { en } from './en';
import { getTg } from '../lib/tg';

export type Locale = 'zh' | 'en';
export type TKey = ZhKey;

const DICTS: Record<Locale, Record<ZhKey, string>> = { zh, en };
const LOCALE_KEY = 'nb_locale';

function detectInitial(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_KEY);
    if (stored === 'zh' || stored === 'en') return stored;
  } catch {
    /* noop */
  }
  // URL hint: ?lang=zh or ?lang=en (sent by the bot when launching the WebApp).
  try {
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (urlLang === 'zh' || urlLang === 'en') return urlLang;
  } catch {
    /* noop */
  }
  // Default to English; only switch to Chinese if the user's Telegram or
  // browser locale is explicitly Chinese.
  const tgLang = getTg()?.initDataUnsafe?.user?.language_code;
  if (typeof tgLang === 'string' && tgLang.toLowerCase().startsWith('zh')) return 'zh';
  const navLang = typeof navigator !== 'undefined' ? navigator.language : '';
  if (navLang && navLang.toLowerCase().startsWith('zh')) return 'zh';
  return 'en';
}

let current: Locale = detectInitial();
const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

export function getLocale(): Locale {
  return current;
}

export function setLocale(l: Locale) {
  if (l === current) return;
  current = l;
  try {
    localStorage.setItem(LOCALE_KEY, l);
  } catch {
    /* noop */
  }
  emit();
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale, getLocale);
}

function render(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) => {
    const v = params[k];
    return v == null ? `{${k}}` : String(v);
  });
}

export function useT() {
  const l = useLocale();
  return (key: TKey, params?: Record<string, string | number>): string =>
    render(DICTS[l][key] ?? key, params);
}

export function t(key: TKey, params?: Record<string, string | number>): string {
  return render(DICTS[current][key] ?? key, params);
}

/** Convert a Neburst language code (zh-CN, en-US) to our Locale union. */
export function neburstLangToLocale(lang: string | undefined | null): Locale {
  if (typeof lang !== 'string') return 'zh';
  return lang.toLowerCase().startsWith('zh') ? 'zh' : 'en';
}

/** Inverse: from our Locale to a Neburst-compatible code. */
export function localeToNeburstLang(l: Locale): string {
  return l === 'zh' ? 'zh-CN' : 'en-US';
}
